import { useBankingAccountDelete } from "Api/Mutations/Accounts/useBankingAccountDelete";
import { usePrimaryBankingAccountMutation } from "Api/Mutations/Accounts/usePrimaryBankingAccountMutation";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { PromptWithAccountInfo } from "Components/BankAccounts/BankAccountList/BankAccountRow/DeleteAccountButton/PromptWithAccountInfo";
import { ReplacementPrimaryAccountSelection } from "Components/BankAccounts/BankAccountList/BankAccountRow/DeleteAccountButton/ReplacementPrimaryAccountSelection";
import { BlDivider } from "Components/Shared/BlDivider";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import {
  type BankAccountListItem,
  BankAccountStatus,
} from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { pickMainBankAccount } from "Utils/AccountUtils";

const DELETE_RESOURCES = Resources.Pages.Content.BankAccounts.DeleteAccount;

type Props = {
  account: BankAccountListItem;
};

export const useAccountDeleteDialog = ({ account }: Props) => {
  const [
    replacementPrimaryAccountPublicID,
    setReplacementPrimaryAccountPublicID,
  ] = useState<string | null>(null);
  const { handleResult, isOpen, open } = useConfirmDialog();
  const { mutate: deleteAccount, isPending } = useBankingAccountDelete();
  const { mutateAsync: savePrimaryAccount } =
    usePrimaryBankingAccountMutation();
  const { data: accountsData } = useBankingAccounts();
  const { t } = useResource();

  // Primary is per currency: deleting a primary only orphans ITS currency rail, so a
  // replacement is offered only among same-currency accounts. Deleting the last account
  // of a currency needs no replacement — the rail simply ends primary-less.
  // The offered accounts and the "is a replacement needed at all" gate must be the SAME set:
  // offering an account the backend refuses to make primary (status guard in
  // SetPrimaryBankAccountCommand) would delete the old primary and leave the rail without one.
  // Active-only is the conservative subset of that guard — whether PreApproved should be offered
  // too is the open FE/BE alignment question, and moving it means moving this one constant.
  const replacementCandidates =
    accountsData?.items.filter(
      ({ bankAccountID, currency, status }) =>
        bankAccountID !== account.bankAccountID &&
        currency === account.currency &&
        status === BankAccountStatus.Active,
    ) || [];

  const needsReplacement =
    account.isPrimary && replacementCandidates.length > 0;

  const newPrimaryAccountPublicID =
    replacementPrimaryAccountPublicID ??
    pickMainBankAccount(replacementCandidates, account.currency)?.publicID ??
    "";

  const deleteConfirmDialog = (
    <ConfirmDialog
      isDestructive
      isOpen={isOpen}
      modalTitle={t(DELETE_RESOURCES.Title)}
      modalContent={
        needsReplacement ? (
          <>
            <PromptWithAccountInfo account={account} />
            <BlDivider />
            <ReplacementPrimaryAccountSelection
              accounts={replacementCandidates}
              selectedPublicID={newPrimaryAccountPublicID}
              onSelect={setReplacementPrimaryAccountPublicID}
            />
          </>
        ) : (
          <PromptWithAccountInfo account={account} />
        )
      }
      handleResult={handleResult(async () => {
        if (needsReplacement && newPrimaryAccountPublicID) {
          await savePrimaryAccount(newPrimaryAccountPublicID);
        }
        deleteAccount(account.bankAccountID);
      })}
    />
  );

  return {
    open,
    isPending,
    deleteConfirmDialog,
  };
};
